import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';
import '../styles/auth.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import api from '../services/api';

const Register = () => {
  // Componente de registro de usuário
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cargo, setCargo] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const [perfis, setPerfis] = useState([]);
  const [idPerfil, setIdPerfil] = useState('');
  const [uf, setUf] = useState('');
  const [idMunicipio, setIdMunicipio] = useState('');
  const [idRegional, setIdRegional] = useState('');
  const [idEscola, setIdEscola] = useState('');

  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [regionais, setRegionais] = useState([]);
  const [escolas, setEscolas] = useState([]);

  const [municipioSearch, setMunicipioSearch] = useState('');
  const [escolaSearch, setEscolaSearch] = useState('');

  // Filtrar municípios com base na busca
  const municipiosFiltrados = useMemo(() => {
    if (!municipioSearch) return municipios;
    return municipios.filter(municipio => 
      municipio.nome_municipio?.toLowerCase().includes(municipioSearch.toLowerCase()) ||
      municipio.id_municipio?.toString().includes(municipioSearch)
    );
  }, [municipios, municipioSearch]);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    return escolas.filter(escola => 
      escola.nome_escola?.toLowerCase().includes(escolaSearch.toLowerCase()) ||
      escola.id_escola?.toString().includes(escolaSearch)
    );
  }, [escolas, escolaSearch]);

  useEffect(() => {
    const fetchPerfis = async () => {
      try {
        const response = await api.get('/public/perfis');
        setPerfis(response.data.data);
      } catch (error) {
        console.error('Erro ao buscar perfis:', error);
      }
    };
    fetchPerfis();

    const fetchEstados = async () => {
      try {
        const response = await api.get('/public/estados');
        setEstados(response.data.data);
      } catch (error) {
        console.error('Erro ao buscar estados:', error);
      }
    };
    fetchEstados();
  }, []);

  useEffect(() => {
    if (uf) {
      const fetchMunicipios = async () => {
        try {
          const response = await api.get(`/public/municipios/by-uf/${uf}`);
          setMunicipios(response.data.data);
        } catch (error) {
          console.error('Erro ao buscar municípios:', error);
        }
      };
      fetchMunicipios();

      const fetchRegionais = async () => {
        try {
          const response = await api.get(`/public/regionais/by-uf/${uf}`);
          setRegionais(response.data.data);
        } catch (error) {
          console.error('Erro ao buscar regionais:', error);
        }
      };
      if (idPerfil === '5') { // Gestor Regional
        fetchRegionais();
      }
    }
  }, [uf, idPerfil]);

  useEffect(() => {
    if (idMunicipio) {
      const fetchEscolas = async () => {
        try {
          const response = await api.get(`/public/escolas/by-municipio/${idMunicipio}`);
          setEscolas(response.data.data);
        } catch (error) {
          console.error('Erro ao buscar escolas:', error);
        }
      };
      if (idPerfil === '4') { // Gestor Escolar
        fetchEscolas();
      }
    }
  }, [idMunicipio, idPerfil]);


  const validateEmail = (email) => {
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validateCPF = (cpf) => {
    return cpfValidator.isValid(cpf);
  };

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setMessage('Formato de email inválido.');
    }
  };

  const handleCpfBlur = () => {
    if (cpf && !validateCPF(cpf)) {
      setMessage('CPF inválido.');
    }
  };

  const validateTelefone = (telefone) => {
    const cleaned = telefone.replace(/\D/g, '');
    return cleaned.length === 10 || cleaned.length === 11;
  };

  const handleTelefoneChange = (e) => {
    const { value } = e.target;
    setMessage('');
    const cleaned = value.replace(/\D/g, '');
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = '(' + cleaned.substring(0, 2);
    }
    if (cleaned.length > 2) {
      formatted += ') ' + cleaned.substring(2, cleaned.length === 11 ? 7 : 6);
    }
    if (cleaned.length > 6 && cleaned.length <= 10) {
      formatted += '-' + cleaned.substring(6, 10);
    }
    if (cleaned.length > 6 && cleaned.length === 11) {
      formatted += '-' + cleaned.substring(7, 11);
    }

    setTelefone(formatted);
  };

  const handleCpfChange = (e) => {
    const { value } = e.target;
    setMessage('');
    const cleaned = value.replace(/\D/g, '');
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = cleaned.substring(0, 3);
    }
    if (cleaned.length > 3) {
      formatted += '.' + cleaned.substring(3, 6);
    }
    if (cleaned.length > 6) {
      formatted += '.' + cleaned.substring(6, 9);
    }
    if (cleaned.length > 9) {
      formatted += '-' + cleaned.substring(9, 11);
    }

    setCpf(formatted);
  };

  const handleTelefoneBlur = () => {
    if (telefone && !validateTelefone(telefone)) {
      setMessage('Telefone inválido. Deve ter 10 ou 11 dígitos.');
    }
  };

  const handleRegister = async () => {
    setMessage('');

    const faltando = [];
    if (!nomeCompleto) faltando.push('Nome');
    if (!email) faltando.push('Email');
    if (!cpf) faltando.push('CPF');
    if (!telefone) faltando.push('Telefone');
    if (!cargo) faltando.push('Cargo');
    if (!senha) faltando.push('Senha');
    if (!confirmSenha) faltando.push('Confirmar Senha');
    if (!idPerfil) faltando.push('Perfil');
    if (faltando.length > 0) {
      setMessage(`Por favor, preencha os campos obrigatórios: ${faltando.join(', ')}`);
      return;
    }

    if (!validateEmail(email)) {
      setMessage('Formato de email inválido.');
      return;
    }

    if (!validateCPF(cpf)) {
      setMessage('CPF inválido.');
      return;
    }

    if (!validateTelefone(telefone)) {
      setMessage('Telefone inválido. Deve ter 10 ou 11 dígitos.');
      return;
    }
    
    if (senha !== confirmSenha) {
      setMessage('As senhas não coincidem.');
      return;
    }

    if (!agreeTerms) {
      setMessage('Você deve concordar com os Termos de Uso e a Política de Privacidade.');
      return;
    }

    try {
      const response = await api.post('/auth/register', {
        nome_completo: nomeCompleto,
        email,
        telefone,
        cargo,
        cpf: cpf.replace(/\D/g, ''), // Envia o CPF limpo para o backend
        senha,
        id_perfil: idPerfil,
        uf,
        id_municipio: idMunicipio,
        id_regional: idRegional,
        codigo_inep: idEscola,
      });
      if (response.data.success) {
        setMessage('Seu cadastro foi realizado com sucesso! Agora ele será analisado pelo gestor da plataforma. Um e-mail será enviado para você assim que sua conta for aprovada. Você poderá acessar o sistema normalmente após a aprovação.');
        setTimeout(() => {
          navigate('/login');
        }, 7000);
      } else {
        setMessage(response.data.error || 'Erro ao registrar.');
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao registrar.');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h2>Criar nova conta</h2>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <div className="form-group">
          <label>Nome completo</label>
          <input
            type="text"
            placeholder="Digite seu nome completo"
            value={nomeCompleto}
            onChange={(e) => setNomeCompleto(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>CPF</label>
          <input
            type="text"
            placeholder="Digite seu CPF"
            value={cpf}
            onChange={handleCpfChange}
            onBlur={handleCpfBlur}
            maxLength="14"
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            placeholder="exemplo@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setMessage('');
            }}
            onBlur={handleEmailBlur}
          />
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Telefone</label>
            <input
              type="text"
              placeholder="(99) 99999-9999"
              value={telefone}
              onChange={handleTelefoneChange}
              onBlur={handleTelefoneBlur}
              maxLength="15"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Cargo/Função</label>
          <input
            type="text"
            placeholder="Ex: Analista, Professor, etc."
            value={cargo}
            onChange={(e) => setCargo(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Perfil</label>
          <select value={idPerfil} onChange={(e) => setIdPerfil(e.target.value)}>
            <option value="">Selecione um perfil</option>
            {perfis && perfis.filter(p => p.id_perfil !== 1).map((perfil) => (
              <option key={perfil.id_perfil} value={perfil.id_perfil}>{perfil.nome_perfil}</option>
            ))}
          </select>
        </div>
        { (idPerfil === '2' || idPerfil === '3' || idPerfil === '4' || idPerfil === '5') && (
          <div className="form-group">
            <label>Estado</label>
            <select value={uf} onChange={(e) => setUf(e.target.value)}>
              <option value="">Selecione um estado</option>
              {estados && estados.map((estado) => (
                <option key={estado.id_sigla_est} value={estado.id_sigla_est}>{estado.nome_estado}</option>
              ))}
            </select>
          </div>
        )}
        { (idPerfil === '3' || idPerfil === '4') && (
          <div className="form-group">
            <label>Município</label>
            <input 
              type="text"
              value={municipioSearch}
              onChange={(e) => {
                setMunicipioSearch(e.target.value);
                const municipioEncontrado = municipios.find(mun => 
                  mun.nome_municipio === e.target.value
                );
                if (municipioEncontrado) {
                  setIdMunicipio(municipioEncontrado.id_municipio);
                } else {
                  setIdMunicipio('');
                }
              }}
              list="municipios-list"
              placeholder="Digite para buscar município..."
              autoComplete="off"
            />
            <datalist id="municipios-list">
              {municipiosFiltrados && municipiosFiltrados.map((municipio) => (
                <option key={municipio.id_municipio} value={municipio.nome_municipio} />
              ))}
            </datalist>
          </div>
        )}
        { idPerfil === '5' && (
          <div className="form-group">
            <label>Regional</label>
            <select value={idRegional} onChange={(e) => setIdRegional(e.target.value)}>
              <option value="">Selecione uma regional</option>
              {regionais && regionais.map((regional) => (
                <option key={regional.id} value={regional.id}>{regional.nome}</option>
              ))}
            </select>
          </div>
        )}
        { idPerfil === '4' && (
          <div className="form-group">
            <label>Escola</label>
            <input 
              type="text"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const escolaEncontrada = escolas.find(esc => 
                  esc.nome_escola === e.target.value
                );
                if (escolaEncontrada) {
                  setIdEscola(escolaEncontrada.id_escola);
                } else {
                  setIdEscola('');
                }
              }}
              list="escolas-list"
              placeholder="Digite para buscar escola..."
              autoComplete="off"
            />
            <datalist id="escolas-list">
              {escolasFiltradas && escolasFiltradas.map((escola) => (
                <option key={escola.id_escola} value={escola.nome_escola} />
              ))}
            </datalist>
          </div>
        )}
        <div className="form-row">
          <div className="form-group" style={{ flex: 1, marginRight: '10px' }}>
            <label>Senha</label>
            <input
              type="password"
              placeholder="Digite uma senha segura"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Confirmar senha</label>
            <input
              type="password"
              placeholder="Confirme sua senha"
              value={confirmSenha}
              onChange={(e) => setConfirmSenha(e.target.value)}
            />
          </div>
        </div>
        <div className="form-row terms">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <label>
            Eu concordo com os <Link to="/terms">Termos de Uso</Link> e a <Link to="/privacy">Política de Privacidade</Link>.
          </label>
        </div>
        <button onClick={handleRegister}>
          Criar conta
        </button>
        <div className="login-link">
          <span>Já tem uma conta? </span>
          <Link to="/login">Faça login</Link>
        </div>
        {message && <p className="auth-message" style={{ color: message.includes('sucesso') ? 'green' : 'red' }}>{message}</p>}
      </div>
    </div>
  );
};

export default Register;
